import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@shared/components/Table';
import { companiesService } from '@shared/services/companiesService';
import { levelOfStructureService } from '@modules/organizationalStructure/levelOfStructure/services/levelOfStructure';

/**
 * Org unit drill-down + positions list (same flow as PersonContract position selection).
 */
export default function PositionPicker({
    onSelect,
    onCancel,
    showSearch = true,
    panelWidth = '100%',
    cancelLabelKey = 'common.cancel',
}) {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [levels, setLevels] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [structuralGroups, setStructuralGroups] = useState([]);
    const [structuralGroupsLoading, setStructuralGroupsLoading] = useState(false);
    const [drillDownData, setDrillDownData] = useState([]);
    const [isDrillDownMode, setIsDrillDownMode] = useState(false);
    const [breadcrumbPath, setBreadcrumbPath] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchQuery, setSearchQuery] = useState({});
    const [searchType, setSearchType] = useState('name');
    const [currentView, setCurrentView] = useState('structuralGroups');
    const [positionsData, setPositionsData] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);

    const structuralGroupsColumns = useMemo(
        () => [
            { key: 'hrOrgUnitLevelsId', label: t('organizationalStructure.structuralGroups.table.headers.level') },
            { key: 'code', label: t('organizationalStructure.structuralGroups.table.headers.code') },
            { key: 'longDescription', label: t('organizationalStructure.structuralGroups.table.headers.name') },
        ],
        [t]
    );

    const positionsColumns = useMemo(
        () => [
            { key: 'positionNumber', label: t('organizationalStructure.structuralGroups.table.headers.number') || 'No.' },
            { key: 'positionName', label: t('organizationalStructure.structuralGroups.table.headers.name') || 'Title' },
            { key: 'ocupantName', label: t('organizationalStructure.structuralGroups.table.headers.occupyingPosition') || 'Occupying position' },
        ],
        [t]
    );

    const searchOptions = useMemo(
        () => [
            { key: 'name', label: 'name' },
            { key: 'code', label: 'code' },
            { key: 'structuralGroupDate', label: 'structuralGroupDate', type: 'date' },
        ],
        []
    );

    const selectedLevelObject = levels.find((level) => String(level.id) === String(selectedLevel));
    const isStructuralGroupsView = currentView === 'structuralGroups';

    useEffect(() => {
        if (!adminBranchId) return;

        const fetchCompany = async () => {
            setSelectedCompany(adminBranchId);
            try {
                setCompaniesLoading(true);
                const company = await companiesService.getSelectedCompany(adminBranchId);
                if (company) {
                    setCompanyOptions([
                        { id: company.id, name: company.name || company.branchName || '' },
                    ]);
                }
            } catch (err) {
                console.error('Error fetching company:', err);
            } finally {
                setCompaniesLoading(false);
            }
        };

        fetchCompany();
    }, [adminBranchId]);

    useEffect(() => {
        const fetchLevels = async () => {
            if (!selectedCompany) {
                setLevels([]);
                setSelectedLevel(null);
                return;
            }

            try {
                setLevelsLoading(true);
                const response = await levelOfStructureService.getAllLevels(selectedCompany);
                setLevels(response || []);
                const levelWithPlanLevel0 = (response || []).find((level) => level.planLevel === 0);
                if (levelWithPlanLevel0) {
                    setSelectedLevel(levelWithPlanLevel0.id);
                } else if (response?.length > 0) {
                    setSelectedLevel(response[0].id);
                }
            } catch (error) {
                console.error('Error fetching levels:', error);
                setLevels([]);
            } finally {
                setLevelsLoading(false);
            }
        };

        fetchLevels();
    }, [selectedCompany]);

    useEffect(() => {
        const fetchStructuralGroups = async () => {
            if (!selectedLevel) {
                setStructuralGroups([]);
                return;
            }

            try {
                setStructuralGroupsLoading(true);
                const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                setStructuralGroups(response || []);
            } catch (error) {
                console.error('Error fetching structural groups:', error);
                setStructuralGroups([]);
            } finally {
                setStructuralGroupsLoading(false);
            }

            setIsDrillDownMode(false);
            setDrillDownData([]);
            setBreadcrumbPath([]);
            setSelectedRecord(null);
            setCurrentView('structuralGroups');
            setPositionsData([]);
        };

        fetchStructuralGroups();
    }, [selectedLevel]);

    const handleCompanyChange = (companyId) => {
        setSelectedCompany(companyId);
    };

    const handleLevelChange = (levelId) => {
        setSelectedLevel(levelId);
    };

    const handleRowClick = async (index) => {
        const currentData = isDrillDownMode ? drillDownData : structuralGroups;

        if (index < 0 || index >= currentData.length) return;

        const structuralGroup = currentData[index];

        try {
            setStructuralGroupsLoading(true);
            const response = await levelOfStructureService.getOrgUnitById(selectedCompany, {
                parentId: structuralGroup.id,
            });

            setDrillDownData(response || []);
            setIsDrillDownMode(true);
            setSelectedRecord(structuralGroup);
            setBreadcrumbPath([
                ...breadcrumbPath,
                {
                    id: structuralGroup.id,
                    title: structuralGroup.longDescription || structuralGroup.code,
                    level: structuralGroup.level,
                },
            ]);
        } catch (err) {
            console.error('Error fetching drill-down data:', err);
            setDrillDownData([]);
            setIsDrillDownMode(false);
        } finally {
            setStructuralGroupsLoading(false);
        }
    };

    const handleBreadcrumbClick = async (breadcrumbItem, index) => {
        try {
            if (breadcrumbItem.id === 'root' || index === 0) {
                setIsDrillDownMode(false);
                setDrillDownData([]);
                setBreadcrumbPath([]);
                setSelectedRecord(null);
                setCurrentView('structuralGroups');
                setPositionsData([]);
                if (selectedLevel) {
                    setStructuralGroupsLoading(true);
                    const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                    setStructuralGroups(response || []);
                    setStructuralGroupsLoading(false);
                }
                return;
            }

            const actualIndex = index - 1;
            const targetItem = breadcrumbPath[actualIndex];

            if (targetItem) {
                setStructuralGroupsLoading(true);
                const response = await levelOfStructureService.getOrgUnitById(selectedCompany, {
                    parentId: targetItem.id,
                });
                setDrillDownData(response || []);
                setIsDrillDownMode(true);
                setSelectedRecord(targetItem);
                setBreadcrumbPath(breadcrumbPath.slice(0, actualIndex + 1));
                setCurrentView('structuralGroups');
                setPositionsData([]);
                setStructuralGroupsLoading(false);
            }
        } catch (err) {
            console.error('Error navigating to breadcrumb level:', err);
            setStructuralGroupsLoading(false);
        }
    };

    const handlePositionHeaderClick = async () => {
        const orgUnitId = selectedRecord?.id;
        if (!orgUnitId) return;

        try {
            setPositionsLoading(true);
            const response = await levelOfStructureService.getPositionsForContract(orgUnitId);
            setPositionsData(response || []);
            setCurrentView('positions');
        } catch (error) {
            console.error('Error fetching positions:', error);
            setPositionsData([]);
        } finally {
            setPositionsLoading(false);
        }
    };

    const handleBackToStructuralGroups = () => {
        setCurrentView('structuralGroups');
        setPositionsData([]);
    };

    const handlePositionRowSelect = (item) => {
        if (item?.id == null || item?.id === '') return;

        const label = [item.positionNumber, item.positionName].filter(Boolean).join(' - ')
            || item.positionName
            || String(item.id);

        onSelect?.(item.id, label, item);
    };

    const handleSearchClick = () => {};

    return (
        <div className="org-unit-picker-panel" style={{ width: panelWidth, alignSelf: 'flex-start' }}>
            <Table
                title=""
                columns={isStructuralGroupsView ? structuralGroupsColumns : positionsColumns}
                data={isStructuralGroupsView ? (isDrillDownMode ? drillDownData : structuralGroups) : positionsData}
                loading={isStructuralGroupsView ? structuralGroupsLoading : positionsLoading}
                showCompanySelect={isStructuralGroupsView}
                showLevelSelect={isStructuralGroupsView}
                searchOptions={showSearch && isStructuralGroupsView ? searchOptions : undefined}
                searchQuery={searchQuery}
                searchType={searchType}
                setSearchQuery={setSearchQuery}
                setSearchType={setSearchType}
                handleSearchClick={handleSearchClick}
                companiesLoading={companiesLoading}
                companyOptions={companyOptions}
                selectedCompany={selectedCompany}
                setSelectedCompany={handleCompanyChange}
                selectedLevel={selectedLevel}
                setSelectedLevel={handleLevelChange}
                levelOptions={levels}
                levelsLoading={levelsLoading}
                showPageSizeSelector={false}
                showPagination={false}
                showPreviousPage={false}
                handleRowClick={isStructuralGroupsView ? handleRowClick : undefined}
                showPositionIcon={isStructuralGroupsView && isDrillDownMode}
                showPositionIconInRows={!isStructuralGroupsView}
                showOrgUnitIcon={!isStructuralGroupsView}
                onPositionIconClick={
                    isStructuralGroupsView ? handlePositionHeaderClick : handlePositionRowSelect
                }
                onOrgUnitIconClick={handleBackToStructuralGroups}
                showBreadcrumb={isStructuralGroupsView && isDrillDownMode}
                breadcrumbPath={
                    isStructuralGroupsView && isDrillDownMode
                        ? [
                              {
                                  id: 'root',
                                  title:
                                      selectedLevelObject?.longDescription
                                      || selectedLevelObject?.name
                                      || 'Root',
                              },
                              ...breadcrumbPath,
                          ]
                        : []
                }
                onBreadcrumbClick={isStructuralGroupsView && isDrillDownMode ? handleBreadcrumbClick : null}
                getData={() => {}}
            />
            <div className="form-controls position-center mt-10">
                <button type="button" className="form-btn btn-red" onClick={onCancel}>
                    {t(cancelLabelKey)}
                </button>
            </div>
        </div>
    );
}
